import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { CollageSetService, CollageSearchParams } from '../../services/collage-set.service';
import { environment } from '../../../environments/environment';
import { DataLayerService } from '../../services/datalayer.service';
import { AppStateService } from '../../services/app-state.service';

interface FilterItem {
  id: any;
  name: string;
  slug?: string;
  activated: boolean;
}

@Component({
  selector: 'app-collage-set',
  templateUrl: './collage-set.component.html'
})
export class CollageSetComponent implements OnInit, OnDestroy {
  isLoading = true;
  products: any[] = [];
  totalRecords = 0;

  currentPage = 1;
  pageSize = 24;

  searchText = '';

  sortBy = 'created_at';
  sortDir = 'desc';
  readonly sorts = [
    { id: 4, label: 'Newest',             sortby: 'created_at',  sortdir: 'desc' },
    { id: 2, label: 'Price low to high',  sortby: 'final_price', sortdir: 'asc'  },
    { id: 3, label: 'Price high to low',  sortby: 'final_price', sortdir: 'desc' },
  ];
  selectedSort = this.sorts[0];

  productTypes: FilterItem[] = [
    { id: 1, name: 'Canvas', slug: 'canvas',  activated: false },
    { id: 2, name: 'Frame',  slug: 'framing', activated: false },
  ];
  collageTypes: FilterItem[] = [];
  noOfFrames:   FilterItem[] = [];
  frameStyles:  FilterItem[] = [];
  priceMin = 1;
  priceMax = 15000;
  readonly priceMaxLimit = 15000;

  facets: any = {};

  showFilters      = false;
  showSortDropdown = false;
  gridCols = 3;

  expandedPanels: Record<string, boolean> = {
    frameType: false, collageType: false, noOfFrames: false, frameStyle: false, pricing: false
  };

  flatDiscount = parseFloat((environment as any).collage_set_flat_discount || '0.15');

  // Track selections for restoring activated flags after async filter load
  private _selPT:  string[] = [];
  private _selCT:  string[] = [];
  private _selNF:  string[] = [];
  private _selFS:  string[] = [];

  private sub = new Subscription();

  constructor(
    public router: Router,
    private route: ActivatedRoute,
    private collageService: CollageSetService,
    private dataLayer: DataLayerService,
    public state: AppStateService,
  ) {}

  ngOnInit() {
    this.loadFilters();

    this.sub.add(this.route.queryParamMap.subscribe(qp => {
      this.currentPage  = Number(qp.get('page')) || 1;
      this.searchText   = qp.get('searchtext') || '';

      // Sort
      const sortby  = qp.get('sortby');
      const sortdir = qp.get('sortdir');
      const matchSort = this.sorts.find(s => s.sortby === sortby && s.sortdir === sortdir);
      this.selectedSort = matchSort || this.sorts[0];
      this.sortBy  = this.selectedSort.sortby;
      this.sortDir = this.selectedSort.sortdir;

      // Price
      this.priceMin = Number(qp.get('pricelt')) || 1;
      this.priceMax = Number(qp.get('pricegt')) || this.priceMaxLimit;

      // Selections
      this._selPT = qp.get('product_type')     ? qp.get('product_type')!.split(',')     : [];
      this._selCT = qp.get('collage_type_slug') ? qp.get('collage_type_slug')!.split(',') : [];
      this._selNF = qp.get('no_of_frames')      ? qp.get('no_of_frames')!.split(',')     : [];
      this._selFS = qp.get('frame_style')       ? qp.get('frame_style')!.split(',')      : [];

      // Restore activated flags (static array + already-loaded dynamic arrays)
      this.productTypes.forEach(i => i.activated = this._selPT.includes(String(i.id)));
      this.collageTypes.forEach(i => i.activated = this._selCT.includes(i.slug!));
      this.noOfFrames.forEach(i  => i.activated = this._selNF.includes(String(i.id)));
      this.frameStyles.forEach(i => i.activated = this._selFS.includes(String(i.id)));

      this.loadProducts();
    }));

    window.scrollTo({ top: 0 });
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  private loadFilters() {
    this.collageService.getCollageTypes().subscribe({
      next: (res: any) => {
        const allowedIds = [4, 10, 11];
        this.collageTypes = (res?.data || [])
          .filter((t: any) => allowedIds.includes(t.id))
          .map((t: any) => ({ id: t.id, name: t.name, slug: t.slug, activated: false }));
        // Restore activated flags for selections already parsed from URL
        this.collageTypes.forEach(i => i.activated = this._selCT.includes(i.slug!));
      },
      error: () => {}
    });

    this.collageService.getCollageFrames().subscribe({
      next: (res: any) => {
        const unique = [...new Set((res?.data || []).map((f: any) => f.no_of_frames))].sort((a: any, b: any) => a - b);
        this.noOfFrames = unique.map((n: any) => ({ id: n, name: n + ' Frames', activated: false }));
        this.noOfFrames.forEach(i => i.activated = this._selNF.includes(String(i.id)));
      },
      error: () => {}
    });

    this.collageService.getFrameStyles().subscribe({
      next: (res: any) => {
        this.frameStyles = (res?.data || []).map((s: any) => ({ id: s.id, name: s.name, activated: false }));
        this.frameStyles.forEach(i => i.activated = this._selFS.includes(String(i.id)));
      },
      error: () => {}
    });
  }

  loadProducts() {
    this.isLoading = true;
    const params: CollageSearchParams = {
      page:    this.currentPage,
      images:  this.pageSize,
      sortby:  this.sortBy,
      sortdir: this.sortDir,
    };

    if (this.searchText) params.searchtext = this.searchText;

    // Read directly from _sel* arrays (set from URL) so filters work even before
    // dynamic filter arrays finish loading from their API calls
    if (this._selPT.length) params.product_type      = this._selPT.join(',');
    if (this._selCT.length) params.collage_type_slug = this._selCT.join(',');
    if (this._selNF.length) params.no_of_frames      = this._selNF.join(',');
    if (this._selFS.length) params.frame_style       = this._selFS.join(',');

    // Both must be sent together for Solr range query
    if (this.priceMin > 1 || this.priceMax < this.priceMaxLimit) {
      params.pricelt = this.priceMin;
      params.pricegt = this.priceMax;
    }

    this.collageService.searchCollageSets(params).subscribe({
      next: (res: any) => {
        this.products     = res?.results || [];
        this.totalRecords = res?.total   || 0;
        this.facets       = res?.facet_field || {};
        this.isLoading    = false;
      },
      error: () => { this.isLoading = false; }
    });
  }

  private syncToUrl() {
    const params: any = { page: this.currentPage };
    if (this.searchText) params.searchtext = this.searchText;

    if (this.selectedSort !== this.sorts[0]) {
      params.sortby  = this.sortBy;
      params.sortdir = this.sortDir;
    }

    const pt = this.productTypes.filter(f => f.activated).map(f => f.id).join(',');
    if (pt) params.product_type = pt;

    const ct = this.collageTypes.filter(f => f.activated).map(f => f.slug).join(',');
    if (ct) params.collage_type_slug = ct;

    const nf = this.noOfFrames.filter(f => f.activated).map(f => f.id).join(',');
    if (nf) params.no_of_frames = nf;

    const fs = this.frameStyles.filter(f => f.activated).map(f => f.id).join(',');
    if (fs) params.frame_style = fs;

    if (this.priceMin > 1 || this.priceMax < this.priceMaxLimit) {
      params.pricelt = this.priceMin;
      params.pricegt = this.priceMax;
    }

    this.router.navigate([], { relativeTo: this.route, queryParams: params, replaceUrl: false });
  }

  // ── Facet helpers ──────────────────────────────────────────────────
  getFacetCount(facetKey: string, itemKey: any): number {
    return this.facets[facetKey]?.[itemKey] || 0;
  }

  isFilterDisabled(facetKey: string, itemKey: any): boolean {
    return !this.facets[facetKey] || !this.facets[facetKey][itemKey];
  }

  // ── Price helpers ──────────────────────────────────────────────────
  getDiscountedPrice(product: any): number {
    const price = product.final_price || 0;
    return Math.round(price - (price * (this.flatDiscount + (product.discount || 0) / 100)));
  }

  getOriginalPrice(product: any): number {
    return Math.round(product.final_price || 0);
  }

  getDiscountPercent(product: any): number {
    return Math.round((this.flatDiscount * 100) + (product.discount || 0));
  }

  // ── Sort ───────────────────────────────────────────────────────────
  onSortChange(sort: any) {
    this.selectedSort = sort;
    this.sortBy  = sort.sortby;
    this.sortDir = sort.sortdir;
    this.currentPage = 1;
    this.syncToUrl();
  }

  // ── Filter toggles ─────────────────────────────────────────────────
  toggleFilter(item: FilterItem) {
    item.activated = !item.activated;
    // Keep _sel* in sync so loadProducts() (called via syncToUrl→subscribe) reads correctly
    this._syncSelArrays();
    this.currentPage = 1;
    this.syncToUrl();
  }

  private _syncSelArrays() {
    this._selPT = this.productTypes.filter(f => f.activated).map(f => String(f.id));
    this._selCT = this.collageTypes.filter(f => f.activated).map(f => f.slug!);
    this._selNF = this.noOfFrames.filter(f => f.activated).map(f => String(f.id));
    this._selFS = this.frameStyles.filter(f => f.activated).map(f => String(f.id));
  }

  get activeFilters(): { label: string; type: string; item: FilterItem }[] {
    const chips: any[] = [];
    this.productTypes.filter(f => f.activated).forEach(f => chips.push({ label: f.name, type: 'product_type', item: f }));
    this.collageTypes.filter(f => f.activated).forEach(f => chips.push({ label: f.name, type: 'collage_type', item: f }));
    this.noOfFrames.filter(f => f.activated).forEach(f => chips.push({ label: f.name, type: 'no_of_frames', item: f }));
    this.frameStyles.filter(f => f.activated).forEach(f => chips.push({ label: f.name, type: 'frame_style', item: f }));
    if (this.priceMin > 1 || this.priceMax < this.priceMaxLimit) {
      chips.push({ label: '₹' + this.priceMin + ' – ₹' + this.priceMax, type: 'price', item: null });
    }
    return chips;
  }

  removeFilter(chip: any) {
    if (chip.type === 'price') {
      this.priceMin = 1;
      this.priceMax = this.priceMaxLimit;
    } else {
      chip.item.activated = false;
    }
    this._syncSelArrays();
    this.currentPage = 1;
    this.syncToUrl();
  }

  clearAllFilters() {
    this.productTypes.forEach(f => f.activated = false);
    this.collageTypes.forEach(f => f.activated = false);
    this.noOfFrames.forEach(f  => f.activated = false);
    this.frameStyles.forEach(f => f.activated = false);
    this.priceMin = 1;
    this.priceMax = this.priceMaxLimit;
    this._syncSelArrays();
    this.currentPage = 1;
    this.syncToUrl();
  }

  private priceDebounce: any;
  onPriceSliderChange() {
    if (this.priceMin > this.priceMax) {
      [this.priceMin, this.priceMax] = [this.priceMax, this.priceMin];
    }
    clearTimeout(this.priceDebounce);
    this.priceDebounce = setTimeout(() => {
      this.currentPage = 1;
      this.syncToUrl();
    }, 500);
  }

  applyFiltersAndClose() {
    this.showFilters = false;
    // Filters already applied instantly via toggleFilter → syncToUrl
  }

  // ── Pagination ─────────────────────────────────────────────────────
  get totalPages(): number { return Math.ceil(this.totalRecords / this.pageSize); }

  get pageNumbers(): number[] {
    const total = this.totalPages;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages: number[] = [1];
    if (this.currentPage > 3) pages.push(-1);
    for (let i = Math.max(2, this.currentPage - 1); i <= Math.min(total - 1, this.currentPage + 1); i++) pages.push(i);
    if (this.currentPage < total - 2) pages.push(-1);
    pages.push(total);
    return pages;
  }

  goToPage(p: number) {
    if (p < 1 || p > this.totalPages || p === this.currentPage) return;
    this.currentPage = p;
    this.syncToUrl();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  navigateToDetail(product: any) {
    this.router.navigate(['/collage-set', product.slug || product.id]);
  }

  toggleWishlist(product: any, e: Event) {
    e.stopPropagation();
    if (!product?.id) return;
    this.state.toggleWishlist(String(product.id), {
      id:             String(product.id),
      name:           product.caption || product.title || product.name || 'Collage set',
      image:          product.list_thumbnail || product.hover_thumbnail || '',
      price:          this.getDiscountedPrice(product),
      wallpaper_type: 'CollageSet',
      slug:           product.slug || String(product.id),
    });
  }

  isWishlisted(product: any): boolean {
    return !!product?.id && this.state.wishlistIds.has(String(product.id));
  }
}
